import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Snowflake, Play, ChevronDown, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StartOperationScreen
 * Ecrã de início de operação — adaptado do layout Samidel React Native (timer.tsx start state)
 * Fundo claro (#FDF6F0), card branco com info, timer 00:00:00, botão Iniciar verde
 */
export default function StartOperationScreen({
  of,              // OF completo
  operacao,        // Operação seleccionada (da lista do lote)
  lote,            // Lote seleccionado
  user,
  isSimulation = false,
  simUser = null,
  loading = false,
  setLoading,
  onBack,          // () => void
  onCongelar,      // () => void
  safeNavigate,
}) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [showCongelarDialog, setShowCongelarDialog] = useState(false);
  const [congelando, setCongelando] = useState(false);
  
  const isArr = lote?.categoria_codigo === 'ARR' || lote?.origem === 'arranjo';
  // ARR usa as regras de contagem da operação subjacente (Tarefa de Arranjo é contagem)
  const isContagem = operacao?.is_contagem || false;
  const isParcial = operacao?.status === 'parcial' || operacao?.status === 'parcial_outros';

  const handleIniciar = async () => {
    if (!of || !operacao || !lote) {
      toast.error('Dados incompletos para iniciar produção');
      return;
    }
    
    setStarting(true);
    setLoading && setLoading(true);
    try {
      await axios.post(`${API}/producao/iniciar`, {
        operador_id: user?.id,
        of_id: of.id,
        operacao_id: operacao.operacao_id || operacao.id,
        lote_id: lote.id,
        quantidade_inicial: null,
      });

      toast.success('Produção iniciada!');
      // Guardar timestamp local para timer instantâneo
      sessionStorage.setItem('producao_inicio_local', new Date().toISOString());
      if (isSimulation) {
        safeNavigate && safeNavigate(`/sim/operador/${simUser}/producao`);
      } else {
        navigate('/operador/producao');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      const status = e?.response?.status;
      if (status === 409 || detail.includes('em pausa')) {
        // Operador em pausa — não pode iniciar
        toast.error('Está em pausa. Retome a operação pausada antes de iniciar outra.');
      } else if (detail.includes('operação mãe') || detail.includes('Lote ARR')) {
        // Regra Lote ARR: a op mãe tem de ser concluída primeiro
        toast.error(detail || 'A operação mãe deste Lote ARR tem de ser concluída antes desta operação.');
      } else if (detail.includes('já tem produção ativa')) {
        // Operador já tem produção ativa — perguntar se quer congelar
        setShowCongelarDialog(true);
      } else {
        toast.error(detail || 'Erro ao iniciar produção');
      }
      console.error(e);
    } finally {
      setStarting(false);
      setLoading && setLoading(false);
    }
  };

  const handleCongelarEIniciar = async () => {
    setCongelando(true);
    try {
      // 1. Congelar a produção ativa atual
      await axios.post(`${API}/producao/congelar`, {
        operador_id: user?.id,
        tempo_decorrido: 0  // O backend calcula baseado no registo
      });
      toast.success('Operação anterior congelada');
      
      // 2. Iniciar a nova operação
      await axios.post(`${API}/producao/iniciar`, {
        operador_id: user?.id,
        of_id: of.id,
        operacao_id: operacao.operacao_id || operacao.id,
        lote_id: lote.id,
        quantidade_inicial: null,
      });
      
      toast.success('Nova produção iniciada!');
      sessionStorage.setItem('producao_inicio_local', new Date().toISOString());
      setShowCongelarDialog(false);
      if (isSimulation) {
        safeNavigate && safeNavigate(`/sim/operador/${simUser}/producao`);
      } else {
        navigate('/operador/producao');
      }
    } catch (e) {
      console.error('Erro ao congelar e iniciar:', e);
      toast.error(e?.response?.data?.detail || 'Erro ao congelar operação');
    } finally {
      setCongelando(false);
    }
  };

  return (
    <div
      className="flex flex-col h-screen"
      style={{ background: '#FDF6F0' }}
    >
      {/* Header branco */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b"
        style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
      >
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
          style={{ background: '#F1F5F9' }}
        >
          <ArrowLeft className="h-5 w-5" style={{ color: '#1E293B' }} />
        </button>
        <h1 className="font-black text-lg" style={{ color: '#1E293B' }}>
          Timer Ativo
        </h1>
        <button
          onClick={onCongelar}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
          style={{ background: '#EFF6FF' }}
        >
          <Snowflake className="h-5 w-5" style={{ color: '#3B82F6' }} />
        </button>
      </div>

      {/* Conteúdo scrollável */}
      <div className="flex-1 overflow-y-auto pb-20">
      {/* Card de informação da tarefa */}
      <div className="px-4 pt-5">
        <div className="rounded-2xl p-4 mb-6" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          {/* OF + CLIENTE + MODELO em grid */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#94A3B8' }}>OF</p>
              <p className="text-3xl font-black leading-none" style={{ color: '#0B3B30' }}>{of?.numero_of || '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#94A3B8' }}>CLIENTE</p>
              <p className="font-extrabold text-sm" style={{ color: '#1E293B' }}>{of?.cliente || '—'}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#94A3B8' }}>MODELO</p>
              <p className="font-extrabold text-sm" style={{ color: '#1E293B' }}>{of?.artigo || '—'}</p>
            </div>
          </div>

          <div className="h-px my-2" style={{ background: '#E2E8F0' }} />

          {/* Operação */}
          <div className="mb-2">
            <p className="text-[10px] font-bold tracking-widest uppercase mb-0.5" style={{ color: '#94A3B8' }}>{isArr ? 'TRABALHO A FAZER' : 'OPERAÇÃO'}</p>
            <div className="flex items-center gap-2">
              <p className="font-extrabold text-base" style={{ color: '#1E293B' }}>
                {isArr
                  ? (lote?.descricao || lote?.observacoes || 'Trabalho de arranjo')
                  : `${operacao?.codigo || operacao?.operacao_codigo || ''} ${operacao?.nome || operacao?.operacao_nome || '—'}`.trim()}
              </p>
              {isContagem && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                  CONTAGEM
                </span>
              )}
              {isArr && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEE2E2', color: '#B91C1C' }}>
                  ARR
                </span>
              )}
              {isParcial && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FFF7ED', color: '#EA580C' }}>
                  PARCIAL
                </span>
              )}
            </div>
          </div>

          {/* Lote */}
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase mb-0.5" style={{ color: '#94A3B8' }}>LOTE</p>
            <p className="font-extrabold text-base" style={{ color: '#1E293B' }}>
              {lote?.codigo_lote || `L${lote?.numero_lote}` || '—'} · {lote?.quantidade_ajustada || lote?.quantidade || 0} un.
            </p>
          </div>
          
          {/* Aviso de operação de contagem */}
          {isContagem && (
            <div className="mt-3 px-3 py-2 rounded-xl flex items-start gap-2" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#2563EB' }} />
              <p className="text-xs font-semibold" style={{ color: '#1E40AF' }}>
                Esta é uma operação de contagem — a quantidade real do lote será ajustada com base na contagem.
              </p>
            </div>
          )}
        </div>

        {/* Área de ação */}
        <div className="flex flex-col items-center py-6">
          <p className="text-base font-bold mb-5" style={{ color: '#0B3B30' }}>
            {isArr ? 'Pronto para arranjar' : (isContagem ? 'Pronto para contar' : 'Pronto para produzir')}
          </p>

          {/* Botão Iniciar */}
          <button
            onClick={handleIniciar}
            disabled={starting || loading}
            className="w-full flex items-center justify-center gap-3 py-5 rounded-full font-black text-xl text-white transition-all active:scale-98 disabled:opacity-50"
            style={{
              background: starting ? '#64748B' : '#0B3B30',
              boxShadow: '0 8px 24px rgba(11,59,48,0.4)',
            }}
            data-testid="btn-iniciar-producao"
          >
            <Play className="h-7 w-7" />
            {starting ? 'A iniciar...' : 'Iniciar'}
          </button>
        </div>
      </div>
      </div>{/* fim do conteúdo scrollável */}

      {/* Dialog: Congelar operação ativa e iniciar nova */}
      <Dialog open={showCongelarDialog} onOpenChange={setShowCongelarDialog}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Operação em Curso
            </DialogTitle>
            <DialogDescription>
              Já tem uma operação em produção. Deseja congelar a operação atual e iniciar esta nova?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <button
              onClick={handleCongelarEIniciar}
              disabled={congelando}
              className="w-full py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50"
              style={{ background: '#3B82F6' }}
              data-testid="btn-congelar-e-iniciar"
            >
              <Snowflake className="h-4 w-4 inline mr-2" />
              {congelando ? 'A congelar...' : 'Congelar e Iniciar Nova'}
            </button>
            <button
              onClick={() => {
                setShowCongelarDialog(false);
                // Ir para a produção ativa
                if (isSimulation) {
                  safeNavigate && safeNavigate(`/sim/operador/${simUser}/producao`);
                } else {
                  navigate('/operador/producao');
                }
              }}
              className="w-full py-3 rounded-xl font-bold transition-all"
              style={{ background: '#F1F5F9', color: '#1E293B' }}
              data-testid="btn-ir-producao-ativa"
            >
              <Play className="h-4 w-4 inline mr-2" />
              Ir para Produção Ativa
            </button>
            <button
              onClick={() => setShowCongelarDialog(false)}
              className="w-full py-2 text-sm text-stone-500"
              data-testid="btn-cancelar-congelar"
            >
              Cancelar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
